import {runInAction} from 'mobx';
import {IRouteStore} from '../../router/routing.infra';
import {getRootStore} from '../../app.bootstrap';
import {GuestController, IGuestController} from '../../components/guest/controller';
import {RolesConstant} from '@techsee/techsee-common/lib/constants/account.constants';
import {LOG_EVENTS} from '@techsee/techsee-common/lib/constants/event-logs.constants';
import {getAppTracer} from '../../../app.tracer';
import {RouteName, RouteNameType} from '../../router/route-names';

const trace = getAppTracer('guest.loader');

export interface GuestUrlParams {
    ott?: string;
    g?: string;
    roomId?: string;
    roomCode?: string;
    audio?: string;
    shortId?: string;
}

interface IObserverLogData {
    roomId?: string;
    shortId: string;
    userName: string;
    lastName: string;
    firstName: string;
    addNewSegment: boolean;
}

export interface GuestObserverDetails {
    deepLink?: string;
    isSf?: boolean;
    isMini?: boolean;
    isEmb?: boolean;
}

export interface IGuestStore extends IRouteStore {
    guestController: IGuestController;
    dispose(): void;
}

export class GuestStore implements IGuestStore {
    public guestController: IGuestController;

    private readonly _token: string;
    private readonly _params: GuestUrlParams;
    private readonly _rootStore = getRootStore();

    constructor(token: string, params: GuestUrlParams, observerDetails?: GuestObserverDetails) {
        this._token = token;
        this._params = params;

        const rootStore = this._rootStore;
        const translate = rootStore.localizationService.translate.bind(rootStore.localizationService);
        const sessionState = {ended: false, isExpired: false};

        this.guestController = new GuestController(
            translate,
            rootStore.domUtilsService,
            () => this.goToLogin(),
            (firstName: string, lastName: string) => this.joinSession(firstName, lastName),
            sessionState
        );

        if (observerDetails) {
            runInAction(() => {
                if (observerDetails.deepLink) this.guestController.deepLink = observerDetails.deepLink;
                if (observerDetails.isSf !== undefined) this.guestController.isSf = observerDetails.isSf;
                if (observerDetails.isMini !== undefined) this.guestController.isMini = observerDetails.isMini;
                if (observerDetails.isEmb !== undefined) this.guestController.isEmb = observerDetails.isEmb;
                this.guestController.isReady = true;
            });
        } else {
            runInAction(() => {
                this.guestController.isReady = true;
            });
        }
    }

    private _buildParams(extra: Record<string, any> = {}): Record<string, any> {
        const {g, roomId, roomCode, audio} = this._params;
        return {
            roomId,
            g,
            roomCode,
            audio,
            observe: true,
            ...(this.guestController?.isMini && {mini: this.guestController.isMini}),
            ...(this.guestController?.isEmb && {emb: this.guestController.isEmb}),
            ...extra
        };
    }

    private _navigateTo(path: RouteNameType, params: Record<string, any>): void {
        void this._rootStore.tsReactStateHelper.navigateTo(path, params);
    }

    goToLogin(): void {
        const {g, roomId} = this._params;
        const rootStore = this._rootStore;

        if (this.guestController?.deepLink) {
            rootStore.eventsLogService.info({
                logType: LOG_EVENTS.observerGoToDeepLinkFromWelcomePage,
                meta: {roomId, guid: g}
            });
            window.location.href = decodeURIComponent(this.guestController.deepLink);
            return;
        }

        rootStore.eventsLogService.info({
            logType: LOG_EVENTS.guestObserverGoToLoginPage,
            meta: {roomId, guid: g}
        });

        if (!rootStore.browserUtilsService.getFromSessionStorage('ssoObserver')) {
            this._navigateTo(RouteName.LOGIN, this._buildParams());
            return;
        }

        rootStore.authHandlerService
            .ssoCurrentUser(
                {
                    getParamValue: (k: string) =>
                        this._params[k as keyof GuestUrlParams] ?? new URL(window.location.href).searchParams.get(k)
                },
                rootStore.userService.getCurrentUser,
                null,
                null,
                ''
            )
            .then(
                (response: any) => {
                    if (response?.embeddedDashboardFailure) {
                        trace.error('GuestState failed - ssoCurrentUser', response.embeddedDashboardFailureError);
                        this._navigateTo(RouteName.EMB_DASHBOARD_ERROR, {});
                        return;
                    }
                    if (response?.redirect) {
                        window.location.href = response.redirect;
                    } else {
                        this._navigateTo(RouteName.INVITE, this._buildParams({audio: this._params.audio}));
                    }
                },
                () => {
                    this._navigateTo(RouteName.LOGIN, this._buildParams());
                }
            );
    }

    joinSession(firstName: string, lastName: string): Promise<void> {
        const {g, roomId, audio} = this._params;
        const room = roomId || g;
        const rootStore = this._rootStore;

        const data: IObserverLogData = {
            shortId: g || '',
            userName: RolesConstant.GUEST_OBSERVER,
            lastName,
            firstName,
            addNewSegment: true
        };
        if (roomId) data.roomId = roomId;

        return rootStore.observationsService
            .guestObserverLogin({token: this._token})
            .then((response: any) => {
                rootStore.authService.setAuthToken({token: response.token});
                return rootStore.observationsService.addObservation(data).then(() => {
                    this._navigateTo(RouteName.INVITE, this._buildParams({room, roomId, g, audio}));
                });
            })
            .catch((err: any) => {
                rootStore.eventsLogService.error({
                    logType: LOG_EVENTS.guestObserverDetailsFailed.type,
                    meta: {
                        roomIdOrGuid: room,
                        error: `Failed login guestObserver ${firstName} ${lastName}, with error: ${err}`
                    }
                });
                this._navigateTo(RouteName.INVITATION_ENDED, {err: true});
            });
    }

    dispose(): void {}
}
